import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Platform,
} from 'react-native';
import DLOModal from './DLOModal';

// Simple text input component that follows the Figma design
const DLOTextInput = ({ 
  label, 
  placeholder, 
  value, 
  onChangeText, 
  required = false 
}: {
  label: string;
  placeholder: string;
  value: string;
  onChangeText: (text: string) => void;
  required?: boolean;
}) => (
  <View style={styles.inputContainer}>
    <View style={styles.labelContainer}>
      <Text style={styles.label}>{label}</Text>
      {required && <Text style={styles.required}>*Required</Text>}
    </View>
    <TextInput
      style={styles.textInput}
      placeholder={placeholder}
      placeholderTextColor="#A0A0A0"
      value={value}
      onChangeText={onChangeText}
      multiline={false}
    />
  </View>
);

export interface DLOAddToFavoritesModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (title: string) => void;
  initialTitle?: string;
  isLoading?: boolean;
}

const DLOAddToFavoritesModal: React.FC<DLOAddToFavoritesModalProps> = ({
  visible,
  onClose,
  onSave,
  initialTitle = '',
  isLoading = false,
}) => {
  const [title, setTitle] = useState(initialTitle);

  const handleSave = () => {
    if (title.trim()) {
      onSave(title.trim());
      setTitle(''); // Reset the input
    }
  };

  const handleClose = () => {
    setTitle(''); // Reset the input when closing
    onClose();
  };

  return (
    <DLOModal
      visible={visible}
      onClose={handleClose}
      title="Add to Favorites"
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onPrimaryPress={handleSave}
      primaryButtonLoading={isLoading}
      primaryButtonDisabled={!title.trim()}
      maxWidth={464} // Based on Figma design
    >
      <DLOTextInput
        label="Schedule Title"
        placeholder="Enter a title for this favorite"
        value={title}
        onChangeText={setTitle}
        required
      />
    </DLOModal>
  );
};

const styles = StyleSheet.create({
  inputContainer: {
    gap: 8,
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
  },
  label: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 18,
  },
  required: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 10,
    fontWeight: '500',
    lineHeight: 14,
  },
  textInput: {
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
    paddingHorizontal: 16,
    paddingVertical: 8,
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    minHeight: 40,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
});

export default DLOAddToFavoritesModal;
